import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  MessageSquare,
  FileText,
  ClipboardCheck,
  Rocket,
  CheckCircle2,
  Calendar,
  ArrowRight,
  Lightbulb,
  Clock,
  Mail,
} from 'lucide-react';

const processSteps = [
  {
    number: '1',
    title: 'Cadrage express',
    duration: '15 min',
    description: 'Un premier échange court pour poser les bases essentielles.',
    items: [
      'Objectifs business',
      'Données disponibles',
      'Budget indicatif',
      'Contraintes techniques',
    ],
    result: 'Checklist complétée → Cadrage clair et réaliste',
    icon: MessageSquare,
    color: '#3dd3ee',
    hasCalendly: true,
  },
  {
    number: '2',
    title: 'First draft',
    duration: 'Sous 24h',
    description: 'Notre regard d\'architecte IA pour éclairer vos arbitrages.',
    items: [
      'Vision de la solution',
      'Options techniques',
      'Estimation budget/délais',
    ],
    result: 'Décision GO / NO GO',
    icon: FileText,
    color: '#fad24b',
  },
  {
    number: '3',
    title: 'Proposition & devis',
    duration: 'Si GO',
    description: 'Formalisation complète du projet.',
    items: [
      'Périmètre précis',
      'Planning détaillé',
      'Budget et conditions',
    ],
    icon: ClipboardCheck,
    color: '#c8aff0',
  },
  {
    number: '4',
    title: 'Livraison clé en main',
    duration: 'Intégré',
    description: 'Solution déployée dans votre environnement.',
    items: [
      'Intégration outils existants',
      'Formation équipes',
      'Accompagnement',
    ],
    result: 'IA adoptée et créatrice de valeur',
    icon: Rocket,
    color: '#b4dc19',
  },
];

const differencePoints = [
  'Process court et orienté décision',
  'Forte exigence sur l\'utilité métier',
  'Du cadrage à l\'industrialisation rapide',
  'Expérience terrain sur projets complexes',
];

const Process = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const goToProjects = () => {
    navigate('/projets');
  };

  return (
    <section id="process" ref={sectionRef} className="relative py-32 bg-[#f8f8f8] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#3dd3ee]/20 to-transparent" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#3dd3ee]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#fad24b]/5 rounded-full blur-[120px]" />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-black/5 text-sm font-medium text-[#61525a] mb-6 shadow-sm">
            <Clock className="w-4 h-4 text-[#3dd3ee]" />
            Commencez en 15 minutes
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0a0a0b] mb-6">
            Comment{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}>
              démarrer
            </span>
            {' '}?
          </h2>
          <p className="text-xl text-[#61525a] max-w-2xl mx-auto">
            Un processus simple et transparent pour transformer vos idées en solutions IA
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-[#3dd3ee] via-[#fad24b] via-[#c8aff0] to-[#b4dc19] rounded-full opacity-30" />

          {/* Steps Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = activeStep === index;

              return (
                <div
                  key={index}
                  className={`relative transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                  onMouseEnter={() => setActiveStep(index)}
                >
                  <div
                    className={`relative h-full bg-white rounded-2xl border p-6 transition-all duration-300 ${
                      isActive
                        ? 'border-transparent shadow-2xl scale-[1.02]'
                        : 'border-black/5 hover:border-transparent hover:shadow-xl'
                    }`}
                  >
                    {/* Top accent */}
                    <div
                      className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl transition-all duration-300"
                      style={{ backgroundColor: step.color, opacity: isActive ? 1 : 0.3 }}
                    />

                    {/* Step Number */}
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold text-white shadow-lg"
                        style={{ backgroundColor: step.color }}
                      >
                        {step.number}
                      </div>
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: `${step.color}20`, color: step.color }}
                      >
                        {step.duration}
                      </span>
                    </div>

                    {/* Icon & Title */}
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="w-5 h-5" style={{ color: step.color }} />
                      <h3 className="text-lg font-bold text-[#0a0a0b]">{step.title}</h3>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-[#61525a] mb-4">{step.description}</p>

                    {/* Items */}
                    <ul className="space-y-2 mb-4">
                      {step.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2
                            className="w-4 h-4 flex-shrink-0"
                            style={{ color: step.color }}
                          />
                          <span className="text-[#61525a]">{item}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Result */}
                    {step.result && (
                      <div
                        className="rounded-xl p-3 mt-auto"
                        style={{ backgroundColor: `${step.color}10` }}
                      >
                        <p className="text-xs font-semibold" style={{ color: step.color }}>
                          → {step.result}
                        </p>
                      </div>
                    )}

                    {/* Calendly CTA for step 1 */}
                    {step.hasCalendly && (
                      <a
                        href="https://calendly.com/saeran-ion/30min"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-xl overflow-hidden"
                        style={{ background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                        <Calendar className="relative z-10 w-4 h-4" />
                        <span className="relative z-10">Réserver un créneau</span>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* What makes the difference */}
        <div
          className={`mt-16 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-[#0a0a0b] rounded-3xl p-8 lg:p-10 border border-white/10">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#fad24b]/20 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-[#fad24b]" />
                </div>
                <h3 className="text-xl font-bold text-white">Ce qui fait la différence</h3>
              </div>
              <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {differencePoints.map((point, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#fad24b] flex-shrink-0" />
                    <span className="text-sm text-white/80">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Use Cases CTA */}
        <div
          className={`mt-8 text-center transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-[#61525a] mb-6">
            Prêt à démarrer ? Choisissez votre mode de contact
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://calendly.com/saeran-ion/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] rounded-xl text-[#0a0a0b] font-medium hover:shadow-xl hover:shadow-[#3dd3ee]/20 transition-all duration-300 hover:scale-105 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
              <Calendar className="relative z-10 w-4 h-4" />
              <span className="relative z-10">Réserver un créneau</span>
            </a>
            <button
              onClick={scrollToContact}
              className="group relative inline-flex items-center gap-2 px-6 py-3 bg-white border border-black/10 rounded-xl text-[#0a0a0b] font-medium hover:shadow-lg transition-all duration-300 hover:scale-105"
            >
              <Mail className="w-4 h-4" />
              <span>Contactez-nous</span>
            </button>
          </div>
          
          <p className="text-[#61525a] mt-8 mb-4">
            Découvrez comment nous avons accompagné nos clients
          </p>
          <button
            onClick={goToProjects}
            className="group relative inline-flex items-center gap-2 px-6 py-3 bg-[#0a0a0b] rounded-xl text-white font-medium hover:bg-[#1e1919] transition-all duration-300 hover:scale-105"
          >
            <span>Voir nos réalisations</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Process;
